import React, { FunctionComponent, useContext, useMemo } from "react";
import { Theme, makeStyles, AccordionSummary, Typography, Grid, fade, lighten } from "@material-ui/core";
import { MenuItemCategory } from "../../../../models/types/category-types";
import { MenuCategoryDictionary } from "../../../../models/categories";
import { CATEGORY_MENU_FONT_SIZE } from "../../../menu/category-item-menu";
import { DARK_TEXT_COLOR } from "../../../../theme/theme";
import { useTranslation } from "react-i18next";
import { ArrowRightIcon } from "../../../../assets/icons/arrows/arrow-right-icon";
import { NutritionContext } from "../../../../pages/nutrition/context/nutrition-context";

const ICON_SIZE = 25;

interface ITopBarSubcategoryListHeaderStyleProps {
  color: string;
  open: boolean;
  active: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  titleContainer: {
    marginLeft: theme.spacing(2),
  },
  title: {
    fontSize: CATEGORY_MENU_FONT_SIZE - 2,
    fontWeight: 700,
    color: ({ color, active, open }: ITopBarSubcategoryListHeaderStyleProps) =>
      active && !open ? color : DARK_TEXT_COLOR,
    marginLeft: theme.spacing(4),
  },
  accordionSummaryRoot: {
    borderRadius: `${theme.spacing(4)}px 0 0 0`,
    marginTop: ({ open }: ITopBarSubcategoryListHeaderStyleProps) => (open ? theme.spacing(1) : 0),
    backgroundColor: ({ color, open }: ITopBarSubcategoryListHeaderStyleProps) =>
      open ? fade(lighten(color!, 0.4), 0.87) : "transparent",
  },
  arrow: {
    marginRight: theme.spacing(6),
    transition: "all 0.2s linear",
    transform: ({ open }: ITopBarSubcategoryListHeaderStyleProps) => (!open ? "rotate(90deg)" : "rotate(-90deg)"),
  },
}));

interface ITopBarSubcategoryListHeaderProps {
  category: MenuItemCategory;
  open: boolean;
}

export const TopBarSubcategoryListHeader: FunctionComponent<ITopBarSubcategoryListHeaderProps> = (props) => {
  const { category, open } = props;
  const { activeCategory } = useContext(NutritionContext);
  const active = useMemo(() => activeCategory === category, [activeCategory, category]);
  const color = useMemo(() => MenuCategoryDictionary[category].color!, [category]);
  const { t } = useTranslation();
  const classes = useStyles({
    color,
    open,
    active,
  });
  const { Icon } = MenuCategoryDictionary[category];

  return (
    <AccordionSummary classes={{ root: classes.accordionSummaryRoot }}>
      <Grid container justify="space-between">
        <Grid item>
          <Grid container alignItems="center" className={classes.titleContainer}>
            {Icon && <Icon height={ICON_SIZE} width={ICON_SIZE} color={active && !open ? color : DARK_TEXT_COLOR} />}
            <Typography key={`title-${category}`} className={classes.title}>
              {t(`menu_item_category.${category}`)}
            </Typography>
          </Grid>
        </Grid>
        <Typography className={classes.arrow}>
          <ArrowRightIcon width={CATEGORY_MENU_FONT_SIZE - 3} color={active && !open ? color : DARK_TEXT_COLOR} />
        </Typography>
      </Grid>
    </AccordionSummary>
  );
};
